import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const FREE_SHIPPING_THRESHOLD = 1500;
const STORAGE_KEY = "shop_canape_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const lineKey = (p) => `${p.productId}-${p.color || ""}-${p.size || ""}`;

  const addItem = (product, { color, size, quantity = 1 } = {}) => {
    const entry = {
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      color: color || undefined,
      size: size || product.sizes?.[0],
      quantity,
    };
    setItems((prev) => {
      const key = lineKey(entry);
      const found = prev.find((i) => lineKey(i) === key);
      if (found) {
        return prev.map((i) => (lineKey(i) === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, entry];
    });
    toast.success(`${product.name} ajouté au panier`, { description: "Ouvrez le panier pour finaliser." });
    setIsOpen(true);
  };

  const updateQty = (key, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, quantity: qty } : i)));
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => lineKey(i) !== key));
  };

  const clearCart = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const shipping = subtotal === 0 || subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : 49;
  const total = subtotal + shipping;
  const freeShippingRemaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return (
    <CartContext.Provider
      value={{
        items, isOpen, setIsOpen, addItem, updateQty, removeItem, clearCart,
        subtotal, count, shipping, total, freeShippingRemaining, lineKey,
        FREE_SHIPPING_THRESHOLD,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
